import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';

// Импорт стилей Swiper
import 'swiper/css';
import 'swiper/css/pagination';
import './MainSlider.css';

// Относительные пути: в проде сайт живёт на том же домене (aison.kz),
// в dev пути идут через src/setupProxy.js на aison.kz.
const SLIDER_DATA_URL = '/data/main_media.json';
const MEDIA_BASE_URL = '';

const MainSlider = () => {
    const [slides, setSlides] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const swiperRef = useRef(null);

    useEffect(() => {
        const fetchSlides = async () => {
            try {
                // Добавляем timestamp, чтобы браузер не кэшировал старый JSON
                const response = await fetch(`${SLIDER_DATA_URL}?t=${new Date().getTime()}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();

                if (data && data.slides && Array.isArray(data.slides)) {
                    // Фильтруем только активные слайды и сортируем по порядку
                    const activeSlides = data.slides
                        .filter(slide => slide.isActive)
                        .sort((a, b) => (a.order || 0) - (b.order || 0));

                    setSlides(activeSlides);
                } else {
                    setSlides([]);
                }
            } catch (err) {
                console.error("Ошибка при получении слайдов:", err);
                setError(err);
                // В случае ошибки можем поставить дефолтное видео или картинку
                setSlides([{
                    image: '/videos/main-video.mp4',
                    isActive: true,
                    order: 1
                }]);
            } finally {
                setLoading(false);
            }
        };

        fetchSlides();
    }, []);

    // Логика для авто-воспроизведения видео
    const handleSlideChange = (swiper) => {
        // Останавливаем все видео
        const videos = swiper.el.querySelectorAll('video');
        videos.forEach(video => {
            if (!video.paused) {
                video.pause();
                video.currentTime = 0; // Возвращаем в начало
            }
        });

        // Запускаем видео на текущем слайде
        const currentSlide = swiper.slides[swiper.activeIndex];
        if (currentSlide) {
            const activeVideo = currentSlide.querySelector('video');
            if (activeVideo) {
                // Важно: на мобилках видео с автоплеем должно быть muted
                activeVideo.play().catch(e => console.log("Autoplay prevented:", e));
                // Если это видео, мы можем приостановить автоматическую прокрутку слайдера
                // пока видео играет
                swiper.autoplay.stop();
                activeVideo.onended = () => {
                    swiper.slideNext();
                    swiper.autoplay.start();
                };
            } else {
                // Если это не видео, запускаем обычный автоплей слайдера
                swiper.autoplay.start();
            }
        }
    };

    if (loading) {
        return <div className="main-slider-placeholder">Загрузка...</div>;
    }

    if (slides.length === 0) {
        return null; // Ничего не показываем, если слайдов нет
    }

    // Если слайд только один, можно не использовать Swiper, а вывести напрямую
    if (slides.length === 1) {
        const slide = slides[0];
        const isVideo = slide.image && slide.image.toLowerCase().endsWith('.mp4');
        // Обрабатываем локальные/абсолютные пути
        const mediaSrc = slide.image.startsWith('http')
          ? slide.image
          : `${MEDIA_BASE_URL}${slide.image}`;

        return (
            <div className="main-slider-single">
                {isVideo ? (
                    <video
                        className="main-slider-media"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                    >
                        <source src={mediaSrc} type="video/mp4" />
                    </video>
                ) : (
                    <img src={mediaSrc} alt="Главный баннер" className="main-slider-media" />
                )}
            </div>
        );
    }

    return (
        <div className="main-slider-container">
            <Swiper
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                modules={[Pagination, Autoplay]}
                spaceBetween={10}
                slidesPerView={1}
                pagination={{ clickable: true }}
                autoplay={{
                    delay: 5000,
                    disableOnInteraction: false,
                }}
                loop={true}
                onSlideChange={handleSlideChange}
                className="main-swiper"
            >
                {slides.map((slide, index) => {
                    const isVideo = slide.image && slide.image.toLowerCase().endsWith('.mp4');
                    // Обрабатываем локальные/абсолютные пути из json
                    let mediaSrc = slide.image;

                    // Относительные пути берём с текущего origin (в dev — через setupProxy.js).
                    if (!mediaSrc.startsWith('http')) {
                        mediaSrc = `${MEDIA_BASE_URL}${mediaSrc}`;
                    }

                    return (
                        <SwiperSlide key={index}>
                            {isVideo ? (
                                <video
                                    className="main-slider-media"
                                    muted
                                    playsInline
                                    loop={false} // Цикличность выключена, чтобы сработал onended
                                >
                                    <source src={mediaSrc} type="video/mp4" />
                                </video>
                            ) : (
                                <img src={mediaSrc} alt={`Слайд ${index + 1}`} className="main-slider-media" />
                            )}
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        </div>
    );
};

export default MainSlider;
